import {
  ConfirmationBullets,
  ConfirmationCard,
  ConfirmationField,
  ConfirmationFields,
  ConfirmationLead,
  ConfirmationNote,
  ConfirmationPanel,
} from "@/shared/foundation/primitives";

import type { ConfirmationContentProps } from "./ClientTools";

interface AgentGoalParams {
  goal_type?: unknown;
  label_vocabulary?: Record<string, unknown>;
  summary_format_spec_prompt?: unknown;
  event_vocabulary?: Record<string, unknown>;
  tag_vocabulary?: Record<string, unknown>;
  collection_enabled?: unknown;
  event_focus_prompt?: unknown;
}

/**
 * The subset of `client_create_agent`'s input this preview reads.
 *
 * Declared here rather than imported from `clientCreateAgent.ts`, which
 * imports this component — a shared definition there would be an import cycle
 * (`import/no-cycle` is an error). Every field is `unknown` so the tool's own
 * input type is assignable to it, which is what lets `createClientTool` accept
 * this component for that tool and, in doing so, check the two agree.
 */
interface AgentParams {
  name?: unknown;
  description?: unknown;
  model_profile?: unknown;
  kickoff_message?: unknown;
  goals?: unknown;
  variables?: unknown;
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() !== "" ? value : undefined;
}

function previewKeys(keys: string[]): string {
  return keys.length <= 3
    ? keys.join(", ")
    : `${keys.slice(0, 3).join(", ")}, +${keys.length - 3} more`;
}

function summarizeGoal(goal: AgentGoalParams): string {
  if (goal.goal_type === "dataset_summary") {
    return "Dataset summary";
  }
  if (goal.goal_type === "dataset_triage") {
    const vocab = goal.label_vocabulary;
    if (vocab && typeof vocab === "object" && !Array.isArray(vocab)) {
      const labels = Object.keys(vocab).filter((k) => k.trim() !== "");
      if (labels.length === 0) {
        return "Dataset triage (no labels yet)";
      }
      return `Dataset triage: ${previewKeys(labels)}`;
    }
    return "Dataset triage";
  }
  if (goal.goal_type === "create_events") {
    const vocab = goal.event_vocabulary;
    const collectionSuffix =
      goal.collection_enabled === true ? " → collection" : "";
    if (vocab && typeof vocab === "object" && !Array.isArray(vocab)) {
      const names = Object.keys(vocab).filter((k) => k.trim() !== "");
      if (names.length === 0) {
        return `Create events (no event types yet)${collectionSuffix}`;
      }
      return `Create events: ${previewKeys(names)}${collectionSuffix}`;
    }
    return `Create events${collectionSuffix}`;
  }
  return "Unknown goal";
}

/**
 * Read-only preview of the draft the model wants to seed into the
 * Agent editor. Surfaces the proposed name, a short description if
 * provided, the count and shape of declared goals, and whether a
 * kickoff message is included. The editor itself is the rich
 * confirmation surface — this card just gives the user enough signal
 * to decide whether to approve the navigation when Auto is off.
 */
export function CreateAgentConfirmation({
  params: p,
}: ConfirmationContentProps<AgentParams>) {
  const name = asString(p.name) ?? "(unnamed)";
  const description = asString(p.description);
  const modelProfile = asString(p.model_profile);
  const kickoff = asString(p.kickoff_message);
  const goals = Array.isArray(p.goals) ? (p.goals as AgentGoalParams[]) : [];
  const variableCount = Array.isArray(p.variables) ? p.variables.length : 0;

  return (
    <ConfirmationPanel label="Create agent preview">
      <ConfirmationLead>
        Open the Agent editor pre-filled with this draft:
      </ConfirmationLead>
      <ConfirmationCard title={name}>
        <ConfirmationFields>
          {description && (
            <ConfirmationField label="Description">
              {description}
            </ConfirmationField>
          )}
          {modelProfile && (
            <ConfirmationField label="Model">{modelProfile}</ConfirmationField>
          )}
          {kickoff && (
            <ConfirmationField label="Kickoff">{kickoff}</ConfirmationField>
          )}
          {goals.length > 0 && (
            <ConfirmationField label={`Goal${goals.length === 1 ? "" : "s"}`}>
              <ConfirmationBullets>
                {goals.map((goal, idx) => (
                  <li key={idx}>{summarizeGoal(goal)}</li>
                ))}
              </ConfirmationBullets>
            </ConfirmationField>
          )}
          {variableCount > 0 && (
            <ConfirmationField label="Variables">
              {variableCount}
            </ConfirmationField>
          )}
        </ConfirmationFields>
      </ConfirmationCard>
      <ConfirmationNote>
        Approving navigates you to the editor — the agent isn&rsquo;t saved
        until you click Create there.
      </ConfirmationNote>
    </ConfirmationPanel>
  );
}
